import React, { useMemo } from "react";
import { marked } from "marked";
import DOMPurify from "dompurify";
import BrandIcon from "./BrandIcon";

// marked 설정: 줄바꿈 → <br>, 코드 하이라이트 비활성화
marked.setOptions({
  breaks: true,
  gfm: true,
});

// 태그에서 앞의 # 접두사 제거 (백엔드에서 #이 포함되어 올 수 있음)
function stripHash(tag) {
  return tag.replace(/^#/, "");
}

/**
 * HTML 태그 내부가 아닌 텍스트 노드에서만 키워드를 하이라이트합니다.
 * 마크다운 변환 후 HTML 태그 속성이 깨지지 않도록 안전하게 처리합니다.
 */
function highlightTags(html, tags) {
  if (!tags || tags.length === 0) return html;

  // HTML을 태그와 텍스트로 분리
  const parts = html.split(/(<[^>]*>)/);

  tags.forEach((tag) => {
    const cleaned = stripHash(tag);
    if (!cleaned) return;
    const regex = new RegExp(
      `(${cleaned.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi"
    );
    for (let i = 0; i < parts.length; i++) {
      // HTML 태그가 아닌 텍스트 부분에서만 치환
      if (!parts[i].startsWith("<")) {
        parts[i] = parts[i].replace(
          regex,
          `<mark class="bg-yellow-200 text-yellow-900 px-1 rounded font-semibold">$1</mark>`
        );
      }
    }
  });

  return parts.join("");
}

export default function AnswerDisplay({ answer, tags, category }) {
  if (!answer) return null;

  const borderColor =
    category === "TAX" ? "border-[#58664a]" : "border-[#1f2933]";
  const tagBg =
    category === "TAX"
      ? "bg-[#eef5e8] text-[#52683f]"
      : "bg-[#f6f1e8] text-[#5f554b]";

  // 마크다운 → HTML 변환 후 키워드 하이라이트 적용
  const renderedHtml = useMemo(() => {
    // AI 답변(마크다운)에 raw HTML이 섞여도 실행되지 않도록 살균 후 렌더 (저장형 XSS 방지)
    const html = DOMPurify.sanitize(marked.parse(answer));
    return highlightTags(html, tags);
  }, [answer, tags]);

  return (
    <div
      className={`w-full p-6 bg-white/85 rounded-2xl shadow-sm border border-[#e2d6c8] border-l-4 ${borderColor}`}
    >
      {/* AI 라벨 */}
      <div className="flex items-center gap-2 mb-4">
        <span className="h-8 w-8 rounded-xl bg-[#f6f1e8] text-[#5f554b] flex items-center justify-center border border-[#e2d6c8]">
          <BrandIcon name={category === "TAX" ? "tax" : "law"} size={17} />
        </span>
        <span className="text-sm font-semibold text-[#6f6258]">AI 답변</span>
      </div>

      {/* 답변 본문 (마크다운 렌더링) */}
      <div
        className="answer-prose text-[#413a33] leading-relaxed"
        dangerouslySetInnerHTML={{ __html: renderedHtml }}
      />

      {/* 키워드 태그 */}
      {tags && tags.length > 0 && (
        <div className="mt-5 pt-4 border-t border-gray-100">
          <p className="text-xs text-[#8a7968] mb-2">핵심 키워드</p>
          <div className="flex flex-wrap gap-2">
            {tags.map((tag, idx) => (
              <span
                key={idx}
                className={`px-3 py-1 rounded-full text-sm font-medium ${tagBg}`}
              >
                #{stripHash(tag)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 면책 안내 */}
      <p className="mt-4 text-xs text-[#8a7968] leading-relaxed">
        * 본 답변은 AI가 생성한 참고용 정보이며, 법적 효력이 없습니다. 정확한
        판단을 위해 전문가 상담을 권장합니다.
      </p>
    </div>
  );
}
